import {
  App,
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { LoginOutlined, LogoutOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { frontDeskApi, type Visitor, type CheckInVisitorInput } from '@/api/frontdesk';
import { hrApi } from '@/api/hr';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

type Mode = 'today' | 'open' | 'range';

interface FormShape {
  visitorName: string;
  cnic?: string;
  purpose: string;
  meetingStaffId?: number;
  authorizedByStaffId?: number;
}

export function VisitorsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FRONT_DESK:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FRONT_DESK:EDIT'));

  const [mode, setMode] = useState<Mode>('today');
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('week'), dayjs().endOf('week')]);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { from, to, openOnly } = (() => {
    if (mode === 'today') return { from: dayjs().format('YYYY-MM-DD'), to: dayjs().format('YYYY-MM-DD'), openOnly: false };
    if (mode === 'open') return { from: dayjs().subtract(7, 'day').format('YYYY-MM-DD'), to: dayjs().format('YYYY-MM-DD'), openOnly: true };
    return { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD'), openOnly: false };
  })();

  const { data, isFetching } = useQuery({
    queryKey: ['frontdesk', 'visitors', { from, to, openOnly }],
    queryFn: () => frontDeskApi.listVisitors(from, to, openOnly),
  });

  const { data: staffPage } = useQuery({
    queryKey: ['hr', 'staff', 'active', 'all'],
    queryFn: () => hrApi.searchStaff({ active: true, size: 500 }),
    staleTime: 60_000,
  });
  const staff = staffPage?.rows ?? [];

  const checkIn = useMutation({
    mutationFn: (values: FormShape) => {
      const body: CheckInVisitorInput = {
        visitorName: values.visitorName.trim(),
        cnic: values.cnic?.trim() || undefined,
        purpose: values.purpose.trim(),
        meetingStaffId: values.meetingStaffId,
        authorizedByStaffId: values.authorizedByStaffId,
      };
      return frontDeskApi.checkInVisitor(body);
    },
    onSuccess: (v) => {
      message.success(`Pass #${v.visitorPassNumber} issued`);
      qc.invalidateQueries({ queryKey: ['frontdesk', 'visitors'] });
      setAdding(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const checkOut = useMutation({
    mutationFn: (id: number) => frontDeskApi.checkOutVisitor(id),
    onSuccess: () => {
      message.success('Visitor checked out');
      qc.invalidateQueries({ queryKey: ['frontdesk', 'visitors'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const stillIn = (data ?? []).filter((v) => !v.checkOutTime).length;

  const columns: TableProps<Visitor>['columns'] = [
    {
      title: 'Pass',
      dataIndex: 'visitorPassNumber',
      width: 90,
      render: (n: number) => <Tag color="processing" style={{ fontFamily: 'monospace' }}>#{n}</Tag>,
    },
    {
      title: 'Visitor',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.visitorName}</Typography.Text>
          {row.cnic && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              CNIC {row.cnic}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      ellipsis: true,
    },
    {
      title: 'Meeting',
      width: 220,
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          {row.meetingStaffName ? (
            <Typography.Text>{row.meetingStaffName}</Typography.Text>
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          )}
          {row.authorizedByStaffName && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              auth: {row.authorizedByStaffName}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'In',
      dataIndex: 'checkInTime',
      width: 150,
      render: (t: string) => dayjs(t).format('D MMM · HH:mm'),
    },
    {
      title: 'Out',
      dataIndex: 'checkOutTime',
      width: 150,
      render: (t: string | null) =>
        t ? (
          dayjs(t).format('D MMM · HH:mm')
        ) : (
          <Tag color="warning">Inside</Tag>
        ),
    },
    {
      title: 'Action',
      width: 120,
      align: 'right',
      render: (_, row) =>
        row.checkOutTime || !canEdit ? null : (
          <Button
            size="small"
            type="text"
            icon={<LogoutOutlined />}
            onClick={() => checkOut.mutate(row.id)}
          >
            Check out
          </Button>
        ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Front Desk"
        title="Visitors"
        description="Sign-in / sign-out with sequential pass numbers."
        count={data ? { value: data.length, label: 'records' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
              Check in
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <Segmented
          value={mode}
          onChange={(v) => setMode(v as Mode)}
          options={[
            { label: 'Today', value: 'today' },
            { label: `Still inside${stillIn ? ` · ${stillIn}` : ''}`, value: 'open' },
            { label: 'Range', value: 'range' },
          ]}
        />
        {mode === 'range' && (
          <DatePicker.RangePicker
            value={range}
            onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
            format="DD MMM YYYY"
            allowClear={false}
          />
        )}
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No visitors in this view" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isFetching}
          columns={columns}
          dataSource={data ?? []}
          pagination={{ pageSize: 25 }}
        />
      )}

      <Modal
        title="Check in visitor"
        open={adding}
        onCancel={() => setAdding(false)}
        onOk={() => form.submit()}
        confirmLoading={checkIn.isPending}
        okText="Check in"
        okButtonProps={{ icon: <LoginOutlined /> }}
        destroyOnHidden
      >
        {adding && (
          <Form form={form} layout="vertical" onFinish={(v) => checkIn.mutate(v)}>
            <Form.Item label="Name" name="visitorName" rules={[{ required: true, max: 200 }]}>
              <Input autoFocus placeholder="e.g. Mr. Ahmed Khan" />
            </Form.Item>
            <Form.Item label="CNIC (optional)" name="cnic" rules={[{ max: 15 }]}>
              <Input placeholder="35202-1234567-8" />
            </Form.Item>
            <Form.Item label="Purpose" name="purpose" rules={[{ required: true, max: 1000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} placeholder="e.g. Parent meeting" />
            </Form.Item>
            <Form.Item label="Meeting staff (optional)" name="meetingStaffId">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}` }))}
              />
            </Form.Item>
            <Form.Item label="Authorized by (optional)" name="authorizedByStaffId">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}` }))}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
